import React, { useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { findMatch, createVeyl } from '../services/matching';
import { useLanguage } from '../i18n';
import { LANGUAGES } from '../i18n/translations';
import { TIMEZONES, getTimezoneLabel } from '../services/timezones';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  .onb-continue-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .onb-continue-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.45) !important;
  }
  .onb-back-btn {
    transition: border-color 0.15s, background-color 0.15s !important;
  }
  .onb-back-btn:hover {
    border-color: rgba(255,255,255,0.25) !important;
    background-color: rgba(255,255,255,0.06) !important;
  }
  .onb-option-btn {
    transition: all 0.15s !important;
  }
  .onb-option-btn:hover {
    border-color: #008081 !important;
    background-color: rgba(0,128,129,0.07) !important;
  }
  .onb-lang-btn {
    transition: all 0.15s !important;
  }
  .onb-lang-btn:hover {
    border-color: rgba(0,128,129,0.5) !important;
    background-color: rgba(0,128,129,0.06) !important;
  }
  .onb-input:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .onb-select:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .onb-checkbox {
    accent-color: #008081;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
    flex-shrink: 0;
    margin-top: 0.15rem;
  }
`;

export default function OnboardingPage({ user, setCurrentPage, setUserData }) {
  const { t, language, setLanguage } = useLanguage();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    goalCategory: '',
    goalDescription: '',
    timezone: '',
    communicationStyle: 'text',
    matchPreference: 'nearby',
    age: '',
    ageAttestationConfirmed: false
  });
  const [ageDenied, setAgeDenied] = useState(false);
  const [loading, setLoading] = useState(false);

  // The timezone list (values like "GMT+3" plus example locations) now lives
  // in ../services/timezones so onboarding and Settings share one source.
  // The saved value is still just "GMT+3" — only the on-screen label changed.

  const categories = [
    { key: 'fitnessHealth',           value: 'Fitness & Health',           emoji: '💪' },
    { key: 'learningSkills',          value: 'Learning & Skills',          emoji: '📚' },
    { key: 'businessCareer',          value: 'Business & Career',          emoji: '💼' },
    { key: 'creativeProjects',        value: 'Creative Projects',          emoji: '🎨' },
    { key: 'relationshipsConnection', value: 'Relationships & Connection', emoji: '🤝' },
    { key: 'homeLifeOrganization',    value: 'Home & Life Organization',   emoji: '🏡' },
    { key: 'moneyFinancialHabits',    value: 'Money & Financial Habits',   emoji: '💰' },
    { key: 'stayingConnected',        value: 'Staying Connected',          emoji: '📞' },
    { key: 'independentLiving',       value: 'Independent Living',         emoji: '🧭' },
    { key: 'techConfidence',          value: 'Tech Confidence',            emoji: '📱' },
  ];

  const profileStep = step - 1;
  const totalProfileSteps = 4;

  // Veyl is 18+ only. Age input determines admission; the checkbox is the
  // explicit, separate attestation required alongside the number itself.
  const isUnder18 = () => {
    const n = parseInt(formData.age, 10);
    if (isNaN(n)) return null; // unknown yet
    return n < 18;
  };

  const canConfirmAge = () => {
    const under18 = isUnder18();
    return under18 === false && formData.ageAttestationConfirmed && formData.age !== '';
  };

  const handleAgeConfirm = () => {
    if (isUnder18()) { setAgeDenied(true); return; }
    if (!canConfirmAge()) return;
    setStep(2);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // setDoc with merge: true rather than updateDoc — updateDoc requires
      // the document to already exist, and onboarding shouldn't be
      // silently dependent on AuthPage.js having already created it
      // successfully. merge: true creates it if missing, or merges into
      // it if present, without wiping any fields already written there
      // (e.g. email, createdAt from sign-up).
      //
      // IMPORTANT: only fields the user is allowed to own are written
      // here. Server-owned fields (isPremium, accountDisabled, strike
      // counters) are deliberately NOT written from the client — the
      // Firestore rules lock them to Cloud Functions, and every reader
      // treats a missing value as false / 0. Writing them from here is
      // what caused "Missing or insufficient permissions" at signup.
      // onboardingComplete is also NOT set here — it is set below, only
      // once the veyl actually exists, so a failed signup can't leave an
      // account marked "finished" with no veyl.
      await setDoc(doc(db, 'users', user.uid), {
        name: formData.name,
        timezone: formData.timezone,
        matchPreference: formData.matchPreference,
        language,
        // Constant going forward — Veyl no longer supports a minor tier.
        // Kept (rather than dropped) so matching.js / groupMatching.js /
        // communityMatching.js and existing Firestore rules that still key
        // off ageGroup keep working unchanged until those are migrated.
        ageGroup: 'adult',
        ageAttestationConfirmed: true,
        ageAttestedAt: serverTimestamp(),
        goalsAchieved: 0,
        blockedUsers: [],
        updatedAt: serverTimestamp()
      }, { merge: true });

      const veylResult = await createVeyl(user.uid, formData);
      if (!veylResult?.id) throw new Error('Failed to create Veyl');
      const veylId = veylResult.id;

      // The veyl exists now, so onboarding is genuinely complete.
      await setDoc(doc(db, 'users', user.uid), {
        onboardingComplete: true,
        updatedAt: serverTimestamp()
      }, { merge: true });

      const matched = await findMatch(user.uid, { ...formData, blockedUsers: [] }, veylId);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = {
        ...userDoc.data(),
        activeVeylId: veylId,
        activeVeyl: {
          id: veylId,
          goalCategory: formData.goalCategory,
          goalDescription: formData.goalDescription,
          timezone: formData.timezone,
          matchPreference: formData.matchPreference,
          status: 'seeking_partner',
          partnerId: null,
          partnerVeylId: null,
        },
        goalCategory: formData.goalCategory,
        goalDescription: formData.goalDescription,
        timezone: formData.timezone,
        matchPreference: formData.matchPreference,
      };
      setUserData(userData);

      setCurrentPage(matched ? 'dashboard' : 'waiting');
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const Blobs = () => (
    <>
      <div style={{ position: 'fixed', top: '-15%', left: '-10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(75,44,94,0.35) 0%, transparent 70%)', pointerEvents: 'none', animation: 'float 8s ease-in-out infinite' }} />
      <div style={{ position: 'fixed', bottom: '-15%', right: '-10%', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,128,129,0.25) 0%, transparent 70%)', pointerEvents: 'none', animation: 'float 10s ease-in-out infinite reverse' }} />
    </>
  );

  const continueBtn = (onClick, disabled, label) => (
    <button className="onb-continue-btn" onClick={onClick} disabled={disabled} style={{ width: '100%', padding: '0.925rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#ffffff', border: 'none', borderRadius: '0.875rem', fontWeight: '700', fontSize: '0.95rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, boxShadow: '0 4px 15px rgba(0,128,129,0.35)' }}>
      {label}
    </button>
  );

  const backBtn = (onClick) => (
    <button className="onb-back-btn" onClick={onClick} style={{ flex: 1, padding: '0.875rem', border: '1px solid rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.6)', borderRadius: '0.875rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>
      ← {t('back')}
    </button>
  );

  const sectionLabel = (text) => (
    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '700', color: 'rgba(255,255,255,0.4)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.09em' }}>
      {text}
    </label>
  );

  const inputStyle = {
    width: '100%', padding: '0.875rem 1rem',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '0.75rem', fontSize: '0.95rem',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#ffffff', boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  // ── Age denied screen (under 18 — no minor tier exists anymore)
  if (ageDenied) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#080f1a', padding: '1rem', position: 'relative', overflow: 'hidden' }}>
        <style>{styleTag}</style>
        <Blobs />
        <div style={{ maxWidth: '32rem', width: '100%', background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', borderRadius: '1.5rem', padding: '3rem 2rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 25px 60px rgba(0,0,0,0.4)', animation: 'fadeInUp 0.4s ease' }}>
          <div style={{ width: '72px', height: '72px', margin: '0 auto 1.5rem', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>🔒</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '0.75rem', color: '#ffffff', letterSpacing: '-0.02em' }}>
            {t('mustBe18')}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, fontSize: '0.9rem' }}>
            {t('mustBe18Desc')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#080f1a', padding: '1rem', position: 'relative', overflow: 'hidden' }}>
      <style>{styleTag}</style>
      <Blobs />

      <div style={{ maxWidth: '42rem', width: '100%', background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', borderRadius: '1.5rem', boxShadow: '0 25px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)', padding: '2.5rem 2rem', border: '1px solid rgba(255,255,255,0.08)', animation: 'fadeInUp 0.4s ease', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: '800', marginBottom: '0.5rem', color: '#ffffff', letterSpacing: '-0.02em' }}>
            {step === 0 ? t('welcomeToVeyl') : step === 1 ? t('ageVerification') : t('setupProfile')}
          </h1>
          {step >= 2 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                {t('stepOf').replace('{step}', profileStep)}
              </span>
              <div style={{ flex: 1, height: '4px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(profileStep / totalProfileSteps) * 100}%`, background: 'linear-gradient(90deg, #008081, #4B2C5E)', borderRadius: '2px', transition: 'width 0.4s ease' }} />
              </div>
              <span style={{ fontSize: '0.72rem', color: '#008081', fontWeight: '700' }}>
                {Math.round((profileStep / totalProfileSteps) * 100)}%
              </span>
            </div>
          )}
        </div>

        {/* STEP 0 — Language */}
        {step === 0 && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <p style={{ color: 'rgba(255,255,255,0.45)', marginBottom: '1.5rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
              {t('chooseLanguageDesc')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '2rem', maxHeight: '340px', overflowY: 'auto', paddingRight: '0.25rem' }}>
              {LANGUAGES.map(lang => {
                const isActive = language === lang.code;
                return (
                  <button key={lang.code} className="onb-lang-btn" onClick={() => setLanguage(lang.code)} style={{ padding: '0.875rem 1rem', border: isActive ? '2px solid #008081' : '2px solid rgba(255,255,255,0.08)', backgroundColor: isActive ? 'rgba(0,128,129,0.12)' : 'rgba(255,255,255,0.03)', borderRadius: '0.875rem', textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '0.15rem', boxShadow: isActive ? '0 0 12px rgba(0,128,129,0.2)' : 'none' }}>
                    <div style={{ fontWeight: '700', color: isActive ? '#008081' : 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>{lang.nativeName}</div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>{lang.name}</div>
                    {isActive && <div style={{ fontSize: '0.68rem', color: '#008081', fontWeight: '700', marginTop: '0.1rem' }}>✓</div>}
                  </button>
                );
              })}
            </div>
            {continueBtn(() => setStep(1), false, t('continue'))}
          </div>
        )}

        {/* STEP 1 — Age input + 18+ attestation */}
        {step === 1 && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ padding: '1.25rem', backgroundColor: 'rgba(0,128,129,0.08)', border: '1px solid rgba(0,128,129,0.2)', borderRadius: '0.875rem', marginBottom: '2rem' }}>
              <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, margin: 0, fontSize: '0.9rem' }}>
                {t('ageVerificationDesc')}
              </p>
            </div>

            {sectionLabel(t('howOldAreYou') || 'How old are you?')}

            <input
              type="number"
              className="onb-input"
              value={formData.age}
              min="1"
              max="120"
              onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
              placeholder={t('enterYourAge')}
              style={{ ...inputStyle, marginBottom: '0.75rem' }}
            />

            {/* Transparent feedback as soon as age is entered */}
            {formData.age !== '' && isUnder18() === true && (
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: '0.75rem',
                marginBottom: '1.5rem',
                backgroundColor: 'rgba(248,113,113,0.08)',
                border: '1px solid rgba(248,113,113,0.2)',
                fontSize: '0.85rem',
                color: '#f87171'
              }}>
                🔒 {t('mustBe18')}
              </div>
            )}

            {/* Explicit attestation — separate affirmative action from the number itself */}
            {formData.age !== '' && isUnder18() === false && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.875rem', marginBottom: '1.25rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="onb-checkbox"
                  checked={formData.ageAttestationConfirmed}
                  onChange={(e) => setFormData(prev => ({ ...prev, ageAttestationConfirmed: e.target.checked }))}
                />
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  {t('ageAttestationLabel')}
                </span>
              </label>
            )}

            {continueBtn(
              handleAgeConfirm,
              formData.age === '' || isUnder18() !== false || !formData.ageAttestationConfirmed,
              t('continue')
            )}

            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', textAlign: 'center', lineHeight: 1.7, margin: '1rem 0 0' }}>
              {t('agreeToTerms')}{' '}
              <a href="/terms" style={{ color: '#008081', textDecoration: 'underline' }}>{t('termsOfService')}</a>
              {' '}{t('and')}{' '}
              <a href="/privacy" style={{ color: '#008081', textDecoration: 'underline' }}>{t('privacyPolicy')}</a>.
              {' '}{t('ageMisrepresentationNote')}
            </p>
          </div>
        )}

        {/* STEP 2 — Name & Timezone */}
        {step === 2 && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              {sectionLabel(t('whatsYourName'))}
              <input type="text" className="onb-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} placeholder={t('namePlaceholder')} />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              {sectionLabel(t('whatsYourTimezone'))}
              <select className="onb-select" value={formData.timezone} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })} style={{ ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.4)' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', paddingRight: '2.5rem' }}>
                <option value="" style={{ backgroundColor: '#1a1f2e' }}>{t('selectTimezone')}</option>
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value} style={{ backgroundColor: '#1a1f2e' }}>
                    {getTimezoneLabel(tz.value)}
                  </option>
                ))}
              </select>
            </div>
            {continueBtn(() => setStep(3), !formData.name || !formData.timezone, t('continue'))}
          </div>
        )}

        {/* STEP 3 — Goal category */}
        {step === 3 && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ marginBottom: '1.75rem' }}>
              {sectionLabel(t('goalCategory'))}
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                {categories.map(cat => {
                  const isActive = formData.goalCategory === cat.value;
                  return (
                    <button key={cat.value} className="onb-option-btn" onClick={() => setFormData({ ...formData, goalCategory: cat.value })} style={{ padding: '1rem 1.25rem', border: isActive ? '2px solid #008081' : '2px solid rgba(255,255,255,0.08)', backgroundColor: isActive ? 'rgba(0,128,129,0.12)' : 'rgba(255,255,255,0.03)', borderRadius: '0.875rem', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.875rem', boxShadow: isActive ? '0 0 12px rgba(0,128,129,0.2)' : 'none' }}>
                      <span style={{ fontSize: '1.5rem' }}>{cat.emoji}</span>
                      <span style={{ fontWeight: '700', color: isActive ? '#008081' : 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>{t(cat.key)}</span>
                      {isActive && <span style={{ marginLeft: 'auto', color: '#008081', fontWeight: '700', fontSize: '0.85rem' }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {backBtn(() => setStep(2))}
              <button className="onb-continue-btn" onClick={() => setStep(4)} disabled={!formData.goalCategory} style={{ flex: 2, padding: '0.875rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#ffffff', border: 'none', borderRadius: '0.875rem', fontWeight: '700', fontSize: '0.9rem', cursor: !formData.goalCategory ? 'not-allowed' : 'pointer', opacity: !formData.goalCategory ? 0.4 : 1, boxShadow: '0 4px 15px rgba(0,128,129,0.3)' }}>
                {t('continue')}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — Match preference */}
        {step === 4 && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ marginBottom: '1.75rem' }}>
              {sectionLabel(t('matchPreference'))}
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {[
                  { value: 'nearby',   labelKey: 'matchNearby',   descKey: 'matchNearbyDesc',   icon: '🌍' },
                  { value: 'anywhere', labelKey: 'matchAnywhere', descKey: 'matchAnywhereDesc', icon: '🌐' },
                ].map(opt => {
                  const isActive = formData.matchPreference === opt.value;
                  return (
                    <button key={opt.value} className="onb-option-btn" onClick={() => setFormData({ ...formData, matchPreference: opt.value })} style={{ padding: '1.25rem', border: isActive ? '2px solid #008081' : '2px solid rgba(255,255,255,0.08)', backgroundColor: isActive ? 'rgba(0,128,129,0.12)' : 'rgba(255,255,255,0.03)', borderRadius: '0.875rem', textAlign: 'left', cursor: 'pointer', boxShadow: isActive ? '0 0 12px rgba(0,128,129,0.2)' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>{opt.icon}</span>
                        <div style={{ fontWeight: '700', color: isActive ? '#008081' : 'rgba(255,255,255,0.85)', fontSize: '1rem' }}>{t(opt.labelKey)}</div>
                        {isActive && <span style={{ marginLeft: 'auto', color: '#008081', fontWeight: '700' }}>✓</span>}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', paddingLeft: '2.25rem', lineHeight: 1.5 }}>{t(opt.descKey)}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {backBtn(() => setStep(3))}
              <button className="onb-continue-btn" onClick={() => setStep(5)} style={{ flex: 2, padding: '0.875rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#ffffff', border: 'none', borderRadius: '0.875rem', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,128,129,0.3)' }}>
                {t('continue')}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5 — Goal description & communication style */}
        {step === 5 && (
          <div style={{ animation: 'fadeInUp 0.3s ease' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              {sectionLabel(t('describeGoal'))}
              <textarea className="onb-input" value={formData.goalDescription} onChange={(e) => setFormData({ ...formData, goalDescription: e.target.value })} style={{ ...inputStyle, height: '7rem', resize: 'vertical' }} placeholder={t('goalPlaceholder')} />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              {sectionLabel(t('communicationStyle'))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {[
                  { value: 'text',      labelKey: 'textMessages',      descKey: 'textMessagesDesc',      icon: '💬' },
                  { value: 'scheduled', labelKey: 'scheduledCheckins', descKey: 'scheduledCheckinsDesc', icon: '📅' },
                ].map(opt => {
                  const isActive = formData.communicationStyle === opt.value;
                  return (
                    <button key={opt.value} className="onb-option-btn" onClick={() => setFormData({ ...formData, communicationStyle: opt.value })} style={{ padding: '1rem', border: isActive ? '2px solid #008081' : '2px solid rgba(255,255,255,0.08)', backgroundColor: isActive ? 'rgba(0,128,129,0.12)' : 'rgba(255,255,255,0.03)', borderRadius: '0.875rem', cursor: 'pointer', textAlign: 'left', boxShadow: isActive ? '0 0 12px rgba(0,128,129,0.2)' : 'none' }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{opt.icon}</div>
                      <div style={{ fontWeight: '700', color: isActive ? '#008081' : 'rgba(255,255,255,0.85)', marginBottom: '0.25rem', fontSize: '0.9rem' }}>{t(opt.labelKey)}</div>
                      <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>{t(opt.descKey)}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {backBtn(() => setStep(4))}
              <button className="onb-continue-btn" onClick={handleComplete} disabled={!formData.goalDescription || loading} style={{ flex: 2, padding: '0.875rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#ffffff', border: 'none', borderRadius: '0.875rem', fontWeight: '700', fontSize: '0.9rem', cursor: (!formData.goalDescription || loading) ? 'not-allowed' : 'pointer', opacity: (!formData.goalDescription || loading) ? 0.4 : 1, boxShadow: '0 4px 15px rgba(0,128,129,0.3)' }}>
                {loading ? t('completingSetup') : t('completeSetup')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}